<template>
  <!-- Beneficiary Photo Update Modal -->
  <div class="modal fade" id="beneficiaryPhotoModal" tabindex="-1" aria-labelledby="beneficiaryPhotoModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header bg-primary text-white">
          <h5 class="modal-title text-white" id="beneficiaryPhotoModalLabel">
            <i class="bx bx-camera me-2"></i>
            Update Beneficiary Photo
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="closeModal" aria-label="Close"></button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <!-- Beneficiary Info -->
          <div class="beneficiary-info-card mb-4 p-3 bg-light rounded">
            <div class="d-flex align-items-center">
              <div class="current-photo-container me-3">
                <img v-if="currentPhotoUrl"
                     :src="currentPhotoUrl"
                     class="rounded-circle border border-2 border-primary"
                     style="width: 60px; height: 60px; object-fit: cover;"
                     :alt="beneficiaryName">
                <div v-else class="bg-secondary rounded-circle d-flex align-items-center justify-content-center"
                     style="width: 60px; height: 60px;">
                  <i class="bx bx-user text-white fs-3"></i>
                </div>
              </div>
              <div>
                <h6 class="mb-1">{{ beneficiaryName }}</h6>
                <p class="mb-0 text-muted small">
                  {{ beneficiaryRelationship }} | {{ beneficiaryAge }} years old
                </p>
              </div>
            </div>
          </div>

          <!-- Photo Source Selection -->
          <div class="photo-source-selection mb-4">
            <label class="form-label">
              <i class="bx bx-photo-album me-1"></i>
              Choose Photo Source
            </label>
            <div class="d-grid gap-2 d-md-flex">
              <button
                  class="btn flex-fill"
                  :class="photoSource === 'upload' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="selectPhotoSource('upload')"
                  :disabled="uploading">
                <i class="bx bx-upload me-2"></i>
                Upload from Device
              </button>
              <button
                  class="btn flex-fill"
                  :class="photoSource === 'camera' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="selectPhotoSource('camera')"
                  :disabled="uploading || !isCameraSupported">
                <i class="bx bx-camera me-2"></i>
                Take Photo
              </button>
            </div>
            <div v-if="!isCameraSupported && photoSource === 'camera'" class="text-warning small mt-2">
              <i class="bx bx-info-circle me-1"></i>
              Camera is not supported on this device or browser
            </div>
          </div>

          <!-- File Upload Section -->
          <div v-if="photoSource === 'upload'" class="file-upload-section">
            <div class="mb-3">
              <label for="beneficiaryPhotoInput" class="form-label">
                <i class="bx bx-image me-1"></i>
                Select Photo File
              </label>
              <input
                  type="file"
                  class="form-control"
                  id="beneficiaryPhotoInput"
                  ref="photoInput"
                  @change="handleFileSelect"
                  accept="image/jpeg,image/jpg,image/png"
                  :disabled="uploading">
              <div class="form-text">
                Accepted formats: JPG, PNG (max 5MB, min 200x200 pixels)
              </div>
            </div>
          </div>

          <!-- Camera Capture Section -->
          <div v-if="photoSource === 'camera'" class="camera-capture-section">
            <!-- Camera View -->
            <div v-if="!cameraCaptured" class="camera-container mb-3">
              <label class="form-label">
                <i class="bx bx-camera me-1"></i>
                Camera Preview
              </label>
              <div class="camera-wrapper position-relative">
                <video
                    ref="cameraVideo"
                    class="w-100 rounded border"
                    :class="{'d-none': !cameraActive}"
                    autoplay
                    playsinline>
                </video>
                <canvas
                    ref="cameraCanvas"
                    class="d-none">
                </canvas>

                <!-- Camera not started overlay -->
                <div v-if="!cameraActive" class="camera-placeholder text-center p-5 bg-dark rounded">
                  <i class="bx bx-camera text-white display-1 mb-3"></i>
                  <button
                      class="btn btn-primary"
                      @click="startCamera"
                      :disabled="cameraLoading">
                    <span v-if="cameraLoading" class="spinner-border spinner-border-sm me-2"></span>
                    <i v-else class="bx bx-play me-2"></i>
                    {{ cameraLoading ? 'Starting Camera...' : 'Start Camera' }}
                  </button>
                </div>

                <!-- Camera controls overlay -->
                <div v-if="cameraActive" class="camera-controls position-absolute bottom-0 start-0 end-0 p-3 bg-dark bg-opacity-75">
                  <div class="d-flex justify-content-center gap-2">
                    <!-- Camera Switch Button (for mobile) -->
                    <button
                        v-if="cameras.length > 1"
                        class="btn btn-outline-light btn-sm"
                        @click="switchCamera"
                        :disabled="cameraSwitching">
                      <i class="bx bx-refresh"></i>
                      Switch Camera
                    </button>

                    <!-- Capture Button -->
                    <button
                        class="btn btn-success"
                        @click="capturePhoto">
                      <i class="bx bx-camera me-2"></i>
                      Capture Photo
                    </button>

                    <!-- Stop Camera -->
                    <button
                        class="btn btn-outline-danger btn-sm"
                        @click="stopCamera">
                      <i class="bx bx-stop"></i>
                      Stop
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Captured Photo Preview -->
            <div v-if="cameraCaptured && capturedPhotoUrl" class="captured-photo-container mb-3">
              <label class="form-label">
                <i class="bx bx-check-circle text-success me-1"></i>
                Captured Photo
              </label>
              <div class="captured-photo-wrapper text-center p-3 border rounded bg-light">
                <img
                    :src="capturedPhotoUrl"
                    class="img-fluid rounded shadow-sm mb-3"
                    style="max-height: 300px;">
                <div class="d-flex justify-content-center gap-2">
                  <button
                      class="btn btn-outline-primary btn-sm"
                      @click="retakePhoto">
                    <i class="bx bx-revision me-1"></i>
                    Retake
                  </button>
                  <button
                      class="btn btn-success btn-sm"
                      @click="acceptCapturedPhoto">
                    <i class="bx bx-check me-1"></i>
                    Use This Photo
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Photo Preview (for both upload and camera) -->
          <div v-if="previewUrl && photoSource === 'upload'" class="mb-3">
            <label class="form-label">Photo Preview</label>
            <div class="photo-preview-container text-center p-3 border rounded">
              <img :src="previewUrl"
                   class="img-fluid rounded shadow-sm"
                   style="max-height: 300px; width: auto;">
              <div class="mt-2">
                <button class="btn btn-sm btn-outline-danger" @click="clearSelection" :disabled="uploading">
                  <i class="bx bx-trash me-1"></i>
                  Remove
                </button>
              </div>
            </div>
          </div>

          <!-- File Info -->
          <div v-if="selectedFile" class="file-info mb-3">
            <div class="alert alert-info py-2">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <i class="bx bx-file me-1"></i>
                  <strong>{{ selectedFile.name || 'Captured Photo' }}</strong>
                </div>
                <div class="text-end">
                  <small>{{ formatFileSize(selectedFile.size) }}</small>
                </div>
              </div>
              <div v-if="imageInfo" class="mt-1">
                <small>Dimensions: {{ imageInfo.width }} x {{ imageInfo.height }} pixels</small>
              </div>
            </div>
          </div>

          <!-- Error Alert -->
          <div v-if="error" class="alert alert-danger" role="alert">
            <i class="bx bx-error-circle me-1"></i>
            {{ error }}
          </div>

          <!-- Success Alert -->
          <div v-if="success" class="alert alert-success" role="alert">
            <i class="bx bx-check-circle me-1"></i>
            {{ success }}
          </div>

          <!-- Photo Guidelines -->
          <div class="photo-guidelines mt-3 p-3 bg-light rounded">
            <h6 class="mb-2">
              <i class="bx bx-info-circle me-1"></i>
              Photo Guidelines
            </h6>
            <ul class="small mb-0">
              <li>Use a clear, recent photo of the beneficiary</li>
              <li>Face should be clearly visible and centered</li>
              <li>Ensure good lighting (avoid backlight)</li>
              <li>Avoid group photos or photos with filters</li>
              <li>Keep the background simple and uncluttered</li>
            </ul>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal" :disabled="uploading">
            <i class="bx bx-x me-1"></i>
            Cancel
          </button>
          <button type="button"
                  class="btn btn-primary"
                  @click="uploadPhoto"
                  :disabled="!selectedFile || uploading">
            <span v-if="uploading" class="spinner-border spinner-border-sm me-2" role="status"></span>
            <i v-else class="bx bx-upload me-1"></i>
            {{ uploading ? 'Uploading...' : 'Upload Photo' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";

export default {
  name: 'BeneficiaryPhotoModal',
  props: {
    cbhiNumber: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      beneficiaryInfo: null,
      beneficiaryId: null,
      beneficiaryName: '',
      beneficiaryRelationship: '',
      beneficiaryAge: 0,
      currentPhotoUrl: null,

      // Photo source selection
      photoSource: 'upload', // 'upload' or 'camera'
      isCameraSupported: false,

      // File upload
      selectedFile: null,
      previewUrl: null,
      imageInfo: null,

      // Camera capture
      cameraActive: false,
      cameraLoading: false,
      cameraCaptured: false,
      cameraSwitching: false,
      capturedPhotoUrl: null,
      mediaStream: null,
      cameras: [],
      currentCameraIndex: 0,

      // Upload state
      uploading: false,
      error: null,
      success: null
    };
  },

  mounted() {
    // Check if camera is supported
    this.checkCameraSupport();
  },

  beforeUnmount() {
    // Clean up camera if active
    this.stopCamera();
  },

  methods: {
    openModal(beneficiary) {
      // Reset state
      this.resetState();

      // Set beneficiary info
      this.beneficiaryInfo = beneficiary;
      this.beneficiaryId = beneficiary.beneficiary_id;
      this.beneficiaryName = this.getBeneficiaryFullName(beneficiary);
      this.beneficiaryRelationship = this.capitalizeFirst(beneficiary.relationship || 'Unknown');
      this.beneficiaryAge = this.calculateAge(beneficiary.date_of_birth);

      // Set current photo URL if exists
      if (beneficiary.profile_photo) {
        this.currentPhotoUrl = this.getBeneficiaryPhotoUrl(beneficiary);
      }

      // Show modal
      const modal = new bootstrap.Modal(document.getElementById('beneficiaryPhotoModal'));
      modal.show();
    },

    closeModal() {
      // Stop camera if active
      this.stopCamera();

      const modal = bootstrap.Modal.getInstance(document.getElementById('beneficiaryPhotoModal'));
      if (modal) {
        modal.hide();
      }
      this.resetState();
    },

    resetState() {
      this.beneficiaryInfo = null;
      this.beneficiaryId = null;
      this.beneficiaryName = '';
      this.beneficiaryRelationship = '';
      this.beneficiaryAge = 0;
      this.currentPhotoUrl = null;
      this.photoSource = 'upload';
      this.selectedFile = null;
      this.previewUrl = null;
      this.imageInfo = null;
      this.cameraActive = false;
      this.cameraLoading = false;
      this.cameraCaptured = false;
      this.capturedPhotoUrl = null;
      this.uploading = false;
      this.error = null;
      this.success = null;

      // Stop camera stream
      this.stopCamera();

      // Clear file input
      if (this.$refs.photoInput) {
        this.$refs.photoInput.value = '';
      }
    },

    // Check camera support
    checkCameraSupport() {
      this.isCameraSupported = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    },

    // Photo source selection
    selectPhotoSource(source) {
      this.photoSource = source;
      this.error = null;
      this.success = null;

      // Clear previous selections
      this.clearSelection();

      // Stop camera if switching away from it
      if (source !== 'camera') {
        this.stopCamera();
      }
    },

    // Camera methods
    async startCamera() {
      this.cameraLoading = true;
      this.error = null;

      try {
        // Get available cameras
        await this.getAvailableCameras();

        // Request camera access
        const constraints = {
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: this.cameras.length > 1 ? 'user' : 'environment'
          },
          audio: false
        };

        this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

        // Set video source
        if (this.$refs.cameraVideo) {
          this.$refs.cameraVideo.srcObject = this.mediaStream;
          this.cameraActive = true;
        }
      } catch (error) {
        console.error('Camera error:', error);
        this.error = 'Failed to access camera. Please ensure camera permissions are granted.';
        this.cameraActive = false;
      } finally {
        this.cameraLoading = false;
      }
    },

    async getAvailableCameras() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        this.cameras = devices.filter(device => device.kind === 'videoinput');
      } catch (error) {
        console.error('Error getting cameras:', error);
        this.cameras = [];
      }
    },

    async switchCamera() {
      if (this.cameras.length <= 1) return;

      this.cameraSwitching = true;
      this.stopCamera();

      // Switch to next camera
      this.currentCameraIndex = (this.currentCameraIndex + 1) % this.cameras.length;

      try {
        const constraints = {
          video: {
            deviceId: { exact: this.cameras[this.currentCameraIndex].deviceId },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        };

        this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

        if (this.$refs.cameraVideo) {
          this.$refs.cameraVideo.srcObject = this.mediaStream;
          this.cameraActive = true;
        }
      } catch (error) {
        console.error('Switch camera error:', error);
        this.error = 'Failed to switch camera';
      } finally {
        this.cameraSwitching = false;
      }
    },

    stopCamera() {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop());
        this.mediaStream = null;
      }

      if (this.$refs.cameraVideo) {
        this.$refs.cameraVideo.srcObject = null;
      }

      this.cameraActive = false;
      this.cameraCaptured = false;
      this.capturedPhotoUrl = null;
    },

    capturePhoto() {
      if (!this.$refs.cameraVideo || !this.$refs.cameraCanvas) return;

      const video = this.$refs.cameraVideo;
      const canvas = this.$refs.cameraCanvas;
      const context = canvas.getContext('2d');

      // Set canvas dimensions to video dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw video frame to canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (blob) {
          // Create file from blob
          const file = new File([blob], 'captured_photo.jpg', { type: 'image/jpeg' });

          // Set as selected file
          this.selectedFile = file;

          // Create preview URL
          this.capturedPhotoUrl = URL.createObjectURL(blob);

          // Set image info
          this.imageInfo = {
            width: canvas.width,
            height: canvas.height
          };

          // Mark as captured
          this.cameraCaptured = true;

          // Stop camera to save resources
          this.stopCamera();

          // Validate dimensions
          if (canvas.width < 200 || canvas.height < 200) {
            this.error = 'Captured image is too small. Please ensure better lighting and distance.';
          }
        }
      }, 'image/jpeg', 0.9);
    },

    retakePhoto() {
      this.cameraCaptured = false;
      this.capturedPhotoUrl = null;
      this.selectedFile = null;
      this.imageInfo = null;
      this.error = null;
      this.startCamera();
    },

    acceptCapturedPhoto() {
      // Photo is already set in selectedFile
      // Just clear any errors
      this.error = null;
    },

    // File upload methods
    handleFileSelect(event) {
      const file = event.target.files[0];

      if (!file) {
        this.clearSelection();
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        this.error = 'Please select a valid image file (JPG or PNG)';
        this.clearSelection();
        return;
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        this.error = 'File size must be less than 5MB';
        this.clearSelection();
        return;
      }

      this.selectedFile = file;
      this.error = null;
      this.success = null;

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewUrl = e.target.result;

        // Get image dimensions
        const img = new Image();
        img.onload = () => {
          this.imageInfo = {
            width: img.width,
            height: img.height
          };

          // Validate dimensions
          if (img.width < 200 || img.height < 200) {
            this.error = 'Image must be at least 200x200 pixels';
            this.clearSelection();
          }
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    clearSelection() {
      this.selectedFile = null;
      this.previewUrl = null;
      this.capturedPhotoUrl = null;
      this.imageInfo = null;
      this.cameraCaptured = false;

      if (this.$refs.photoInput) {
        this.$refs.photoInput.value = '';
      }
    },

    async uploadPhoto() {
      if (!this.selectedFile || this.uploading) {
        return;
      }

      this.uploading = true;
      this.error = null;
      this.success = null;

      const formData = new FormData();
      formData.append('function', 'updateBeneficiaryPhoto');
      formData.append('beneficiaryId', this.beneficiaryId);
      formData.append('cbhiNumber', this.cbhiNumber);
      formData.append('photo', this.selectedFile);

      try {
        const response = await execute(formData, authUrl);

        if (response.data.success) {
          this.success = response.data.message || 'Photo uploaded successfully';

          // Emit success event with photo details
          this.$emit('photo-updated', {
            success: true,
            beneficiaryId: this.beneficiaryId,
            photoPath: response.data.data.photoPath,
            photoUrl: response.data.data.photoUrl,
            message: this.success
          });

          // Close modal after short delay
          setTimeout(() => {
            this.closeModal();
          }, 1500);
        } else {
          this.error = response.data.message || 'Failed to upload photo';
        }
      } catch (error) {
        console.error('Upload error:', error);
        this.error = error.response?.data?.message || 'An error occurred while uploading the photo';

        // Emit error event
        this.$emit('photo-error', {
          beneficiaryId: this.beneficiaryId,
          message: this.error
        });
      } finally {
        this.uploading = false;
      }
    },

    // Utility methods
    getBeneficiaryFullName(beneficiary) {
      return `${beneficiary.first_name || ''} ${beneficiary.middle_name || ''} ${beneficiary.surname || ''}`.trim();
    },

    getBeneficiaryPhotoUrl(beneficiary) {
      if (beneficiary.profile_photo) {
        if (beneficiary.profile_photo.startsWith('http')) {
          return beneficiary.profile_photo;
        }
        return `https://api.paysol.co.ke/cbhi/uploads/beneficiary-photos/${beneficiary.profile_photo}`;
      }
      return null;
    },

    calculateAge(dateOfBirth) {
      if (!dateOfBirth) return 0;
      const dob = new Date(dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }

      return age;
    },

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    }
  }
};
</script>

<style scoped>
/* Modal Styles */
.modal-header.bg-primary {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%) !important;
}

/* Photo source selection */
.photo-source-selection .btn {
  padding: 0.75rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.photo-source-selection .btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Camera styles */
.camera-wrapper {
  background: #000;
  border-radius: 0.5rem;
  overflow: hidden;
  min-height: 300px;
}

.camera-wrapper video {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 0.5rem;
}

.camera-placeholder {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.camera-controls {
  background: linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.5)) !important;
}

/* Captured photo */
.captured-photo-wrapper {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.captured-photo-wrapper img {
  max-width: 100%;
  height: auto;
}

/* Beneficiary Info Card */
.beneficiary-info-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 1px solid #e9ecef;
}

/* Photo Preview Container */
.photo-preview-container {
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6 !important;
  transition: all 0.3s ease;
}

.photo-preview-container:hover {
  border-color: #2c5aa0 !important;
  background-color: #f1f3f4;
}

/* File Info */
.file-info .alert {
  background: linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%);
  border-color: #b8daff;
}

/* Photo Guidelines */
.photo-guidelines {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-left: 3px solid #2c5aa0;
}

.photo-guidelines ul {
  padding-left: 20px;
  margin-bottom: 0;
}

.photo-guidelines li {
  margin-bottom: 4px;
}

/* Buttons */
.btn {
  border-radius: 0.375rem;
  transition: all 0.3s ease;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn-primary {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%);
  border-color: #2c5aa0;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #1e3f70 0%, #357abd 100%);
  border-color: #1e3f70;
}

/* Alerts */
.alert {
  border-radius: 0.5rem;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Spinner */
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-dialog {
    margin: 0.5rem;
    max-width: calc(100% - 1rem);
  }

  .camera-wrapper {
    min-height: 250px;
  }

  .photo-guidelines {
    font-size: 0.875rem;
  }

  .file-info .alert {
    padding: 0.5rem;
  }

  .photo-source-selection .btn {
    padding: 0.5rem;
    font-size: 0.9rem;
  }
}

/* Mobile camera specific */
@media (max-width: 576px) {
  .camera-controls {
    padding: 0.75rem !important;
  }

  .camera-controls .btn {
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
  }
}
</style>